import { DialogHeight } from "../models/dialog-height.model";
import { DialogPresentation } from "../models/dialog-presentation.model";
import { DialogSize } from "../models/dialog-size.model";

const MAIN_CLASS_ALIASES: Record<string, string[]> = {
	"p-0": ["azm-dialog-panel--flush"],
	"border-0": ["border-0"],
	"!h-max": ["h-max-content", "azm-dialog-panel--auto"],
	"!h-min": ["azm-dialog-panel--min"],
	"h-max-content": ["h-max-content"],
	"dialog-center": ["dialog-center"],
	"content-padding": ["content-padding"],
	"pb-header-0": ["pb-header-0"],
};

const MAIN_PANEL_PRESETS = {
	small: ["small", "azm-dialog-panel--flush", "azm-dialog-panel--auto", "border-0"],
	medium: ["medium", "azm-dialog-panel--flush", "azm-dialog-panel--auto", "border-0"],
	mediumScrollable: ["medium", "azm-dialog-panel--flush", "azm-dialog-panel--scrollable", "border-0"],
	addDefendantStatement: [
		"medium",
		"dialog-center",
		"azm-dialog-panel--flush",
		"azm-dialog-panel--scrollable",
		"add-defendant-statement-dialog",
		"border-0",
	],
	large: ["large", "azm-dialog-panel--flush", "azm-dialog-panel--auto", "border-0"],
	smallCenter: ["small", "dialog-center", "azm-dialog-panel--flush", "azm-dialog-panel--min", "border-0", "pb-header-0"],
	mediumCenter: ["medium", "dialog-center", "azm-dialog-panel--flush", "azm-dialog-panel--min", "border-0", "pb-header-0"],
	mediumCenterMax: ["medium", "dialog-center", "azm-dialog-panel--flush", "azm-dialog-panel--auto", "border-0"],
	termsLegal: ["large", "dialog-center", "terms-conditions", "azm-dialog-panel--flush", "border-0"],
	confirm: ["confirm-action-dialog", "azm-dialog-panel--flush", "azm-dialog-panel--auto", "h-max-content", "border-0"],
	confirmLogout: [
		"confirm-action-dialog",
		"confirm-action-dialog--logout",
		"azm-dialog-panel--flush",
		"azm-dialog-panel--auto",
		"h-max-content",
		"border-0",
	],
	confirmFigma: ["confirm-action-dialog--figma", "azm-dialog-panel--flush", "azm-dialog-panel--auto", "h-max-content", "border-0"],
	confirmDirectorateBlocked: [
		"confirm-action-dialog",
		"confirm-action-dialog--directorate-blocked",
		"azm-dialog-panel--flush",
		"azm-dialog-panel--auto",
		"h-max-content",
	],
	confirmCancel: [
		"small",
		"dialog-center",
		"azm-dialog-panel--flush",
		"confirm-action-dialog",
		"confirm-action-dialog--cancel",
		"h-max-content",
		"border-0",
	],
	confirmWarning: [
		"dialog-center",
		"confirm-warning-dialog",
		"azm-dialog-panel--flush",
		"azm-dialog-panel--auto",
		"h-max-content",
		"border-0",
	],
	cancelRequest: [
		"small",
		"dialog-center",
		"azm-dialog-panel--flush",
		"azm-dialog-panel--auto",
		"confirm-action-dialog",
		"confirm-action-dialog--logout",
		"cancel-request-dialog",
		"h-max-content",
		"border-0",
	],
	requestSuccess: [
		"small",
		"success-dialog",
		"request-success-dialog",
		"dialog-center",
		"azm-dialog-panel--flush",
		"azm-dialog-panel--auto",
		"h-max-content",
		"border-0",
	],
	otp: ["otp-dialog-panel", "azm-dialog-panel--flush"],
	confirmMatch: ["azm-dialog-panel--confirm-sm", "azm-dialog-panel--flush", "azm-dialog-panel--auto", "h-max-content", "border-0"],
	studyMedium: [
		"medium",
		"azm-dialog-panel--study",
		"azm-dialog-panel--flush",
		"azm-dialog-panel--auto",
		"h-max-content",
		"content-padding",
		"pb-header-0",
		"border-0",
	],
	largeResubmit: ["large", "azm-dialog-panel--flush", "content-padding", "pb-header-0", "border-0"],
} as const;

export type MainDialogPanelPreset = keyof typeof MAIN_PANEL_PRESETS;

export function mainDialogPanel(preset: MainDialogPanelPreset, ...extra: string[]): string[] {
	return applyMainDialogParityClasses([...MAIN_PANEL_PRESETS[preset], ...extra]);
}

function applyMainDialogParityClasses(classes: string[]): string[] {
	const set = new Set(classes.filter(Boolean));

	if (set.has("azm-dialog-panel--study")) {
		set.add("content-padding");
		set.add("pb-header-0");
		set.add("border-0");
		set.add("azm-dialog-panel--flush");
		set.add("h-max-content");
		set.add("azm-dialog-panel--auto");
	}

	if (set.has("confirm-action-dialog") || set.has("confirm-accept-reply-dialog") || set.has("confirm-reject-reply-dialog")) {
		set.add("azm-dialog-panel--flush");
		set.add("h-max-content");
		set.add("azm-dialog-panel--auto");
		set.add("border-0");
	}

	if (set.has("confirm-warning-dialog")) {
		set.add("azm-dialog-panel--flush");
		set.add("h-max-content");
		set.add("azm-dialog-panel--auto");
		set.add("border-0");
	}

	if (set.has("confirm-action-dialog--figma")) {
		set.add("azm-dialog-panel--flush");
		set.add("h-max-content");
		set.add("azm-dialog-panel--auto");
		set.add("border-0");
	}

	if (set.has("azm-dialog-panel--flush") && (set.has("medium") || set.has("small") || set.has("large"))) {
		set.add("border-0");
	}

	if (set.has("content-padding") || set.has("pb-header-0")) {
		set.add("border-0");
	}

	return [...set];
}

export function normalizeMainDialogPanelClasses(panelClass: string | string[] | undefined): string[] {
	const classes = Array.isArray(panelClass)
		? panelClass.flatMap((value) => String(value).split(/\s+/))
		: String(panelClass ?? "")
				.split(/\s+/)
				.filter(Boolean);

	const normalized: string[] = [];

	for (const panel of classes) {
		if (panel === DialogSize.Small || panel === DialogSize.Medium || panel === DialogSize.Large) {
			normalized.push(panel);
			continue;
		}

		if (panel === DialogHeight.MaxContent || panel === "h-max-content" || panel === "!h-max") {
			normalized.push("h-max-content", "azm-dialog-panel--auto");
			continue;
		}

		if (panel === "azm-dialog-panel--min" || panel === "!h-min") {
			normalized.push("azm-dialog-panel--min");
			continue;
		}

		if (panel === DialogPresentation.Sheet) {
			normalized.push(DialogPresentation.Sheet);
			continue;
		}

		const alias = MAIN_CLASS_ALIASES[panel];
		if (alias) {
			normalized.push(...alias);
			continue;
		}

		normalized.push(panel);
	}

	return applyMainDialogParityClasses([...new Set(normalized.filter(Boolean))]);
}
